import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef } from 'react';
import { useController, useForm, useFormState, useWatch } from 'react-hook-form';
import type { Control, DefaultValues, FieldPath, Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  BUSINESS_TYPES,
  CUSTOMER_TYPES,
  GRACE_STATUSES_AFTER,
  GRACE_STATUSES_DURING,
  NLAD_SERVICE_TYPES,
  PLAN_TYPES,
  SALES_CHANNELS,
  planInputSchema,
} from '@app/shared';
import type { PlanData, PlanDto, PlanInput, SalesChannel } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import {
  CheckboxInput,
  MoneyInput,
  NumberInput,
  SelectInput,
  TextInput,
  YesNoInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { ApiError } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { useCarrierCodeOptions, useCreatePlan, usePlan, useUpdatePlan } from './api';
import type { CarrierCodeOption } from './api';
import { usePlanOptions } from './lookups';
import { STATE_OPTIONS, withCurrent } from './options';

const LIST_PATH = '/settings/plans';
/** The reference shows three "amount/percent + transaction code" lines. */
const TAX_LINE_COUNT = 3;
const MAX_LINES = 10;

const CUSTOMER_TYPE_OPTIONS = CUSTOMER_TYPES.map(([code, name]) => ({
  value: code,
  label: `${name}(${code})`,
}));
const BUSINESS_TYPE_OPTIONS = BUSINESS_TYPES.map(([code, name]) => ({
  value: code,
  label: `${name}(${code})`,
}));

const emptyTaxLines = (lines: PlanInput['taxLines'] = []) =>
  Array.from(
    { length: TAX_LINE_COUNT },
    (_, i) => lines[i] ?? { amountOrPercent: '', transCode: '' },
  );

const ordinal = (n: number) => ['1st', '2nd', '3rd'][n - 1] ?? `${n}th`;

/** Per-line price inputs to show: one per line of a family plan, none for a single line. */
const lineCountOf = (maxLines: unknown): number =>
  typeof maxLines === 'number' && maxLines > 1 ? Math.min(maxLines, MAX_LINES) : 0;

const schemaResolver = zodResolver(planInputSchema);
/**
 * React Hook Form shows a field's default value again whenever its value is `undefined`, so an
 * emptied number input would jump back to the saved number. The number inputs therefore hold
 * `null` while empty; the schema only knows "missing", so `null` is translated before validating.
 */
const resolver: Resolver<PlanInput> = (values, context, options) =>
  schemaResolver(
    {
      ...values,
      maxLines: values.maxLines ?? undefined,
      gracePeriodDays: values.gracePeriodDays ?? undefined,
      linePricesCents: values.linePricesCents?.map((cents) => cents ?? undefined),
    } as PlanInput,
    context,
    options,
  );

/** Required selects start empty so the user has to choose, as in the reference. */
const NEW_PLAN: DefaultValues<PlanInput> = {
  name: '',
  description: '',
  channels: [],
  taxable: true,
  customerType: '01',
  businessType: '14',
  priceIncludesTax: false,
  taxLines: emptyTaxLines(),
  isUnlimited: false,
  unlimitedData: false,
  unlimitedText: false,
  unlimitedMinutes: false,
  maxLines: 1,
  linePricesCents: [],
  tribal: null,
  byod: null,
  statusDuringGrace: 'Suspend',
  statusAfterGrace: 'Suspend',
  fallbackPlanId: null,
  states: [],
};

const toFormValues = (plan: PlanDto): PlanInput => ({
  name: plan.name,
  description: plan.description,
  type: plan.type,
  channels: plan.channels,
  taxable: plan.taxable,
  customerType: plan.customerType,
  businessType: plan.businessType,
  priceIncludesTax: plan.priceIncludesTax,
  taxLines: emptyTaxLines(plan.taxLines),
  isUnlimited: plan.isUnlimited,
  unlimitedData: plan.unlimitedData,
  unlimitedText: plan.unlimitedText,
  unlimitedMinutes: plan.unlimitedMinutes,
  carrierCodeId: plan.carrierCode.id,
  moreCarrierCodeIds: plan.moreCarrierCodes.map((c) => c.id),
  priceCents: plan.priceCents,
  activationFeeCents: plan.activationFeeCents,
  maxLines: plan.maxLines,
  linePricesCents: plan.linePricesCents,
  tribal: plan.tribal,
  display: plan.display,
  notifyCustomer: plan.notifyCustomer,
  byod: plan.byod,
  nladServiceType: plan.nladServiceType,
  gracePeriodDays: plan.gracePeriodDays,
  statusDuringGrace: plan.statusDuringGrace,
  statusAfterGrace: plan.statusAfterGrace,
  fallbackPlanId: plan.fallbackPlan?.id ?? null,
  states: plan.states,
});

/**
 * ZIP carrier routing (owner, 2026-11-05): the plan's wholesale code on each other carrier, as
 * the reference labels it, "Carrier Setup (TMB)". A carrier left on "Not sold" does not sell it.
 */
function OtherCarrierCodes({
  control,
  options,
}: {
  control: Control<PlanInput>;
  options: CarrierCodeOption[];
}) {
  const { field } = useController({ control, name: 'moreCarrierCodeIds' });
  const { errors } = useFormState({ control, name: 'moreCarrierCodeIds' });
  const mainId = useWatch({ control, name: 'carrierCodeId' });
  const value: string[] = Array.isArray(field.value) ? field.value : [];
  const carrierOf = (codeId: string) => options.find((o) => o.id === codeId)?.carrierCode;
  const main = carrierOf(mainId);
  const carriers = [...new Set(options.map((o) => o.carrierCode))].filter((c) => c !== main).sort();
  if (!carriers.length) return null;
  const set = (carrier: string, codeId: string) =>
    field.onChange([
      ...value.filter((id) => carrierOf(id) !== carrier),
      ...(codeId ? [codeId] : []),
    ]);
  const fieldErrors = errors.moreCarrierCodeIds as Array<{ message?: string }> | undefined;
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {carriers.map((carrier) => {
        const chosen = value.find((id) => carrierOf(id) === carrier) ?? '';
        const error = chosen ? fieldErrors?.[value.indexOf(chosen)]?.message : undefined;
        return (
          <TextField
            key={carrier}
            select
            label={`Carrier Setup (${carrier})`}
            value={chosen}
            onChange={(e) => set(carrier, e.target.value)}
            error={Boolean(error)}
            helperText={error ?? `Its wholesale code on ${carrier}, to sell it there too.`}
          >
            <MenuItem value="">Not sold on {carrier}</MenuItem>
            {options
              .filter((o) => o.carrierCode === carrier)
              .map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.code}
                </MenuItem>
              ))}
          </TextField>
        );
      })}
    </Box>
  );
}

/** Sales-channel permission checkboxes, stored as the list of ticked channels. */
function ChannelsInput({ control }: { control: Control<PlanInput> }) {
  const { field, fieldState } = useController({ control, name: 'channels' });
  const value: SalesChannel[] = Array.isArray(field.value) ? field.value : [];
  const toggle = (channel: SalesChannel, on: boolean) =>
    // Rebuilt from the full list so the stored order never depends on the click order.
    field.onChange(SALES_CHANNELS.filter((c) => (c === channel ? on : value.includes(c))));
  return (
    <FormControl component="fieldset" variant="standard" error={Boolean(fieldState.error)}>
      <FormLabel component="legend">Sales channel permissions</FormLabel>
      <FormGroup row>
        {SALES_CHANNELS.map((channel) => (
          <FormControlLabel
            key={channel}
            label={channel}
            control={
              <Checkbox
                size="small"
                checked={value.includes(channel)}
                onChange={(e) => toggle(channel, e.target.checked)}
              />
            }
          />
        ))}
      </FormGroup>
      {fieldState.error ? <FormHelperText>{fieldState.error.message}</FormHelperText> : null}
    </FormControl>
  );
}

/**
 * Multi-select of states. The form stores `{ state, status }` rows: a state the plan already
 * has keeps its LIVE/DEMO status, a newly added one starts as DEMO.
 */
function StatesInput({
  control,
  saved,
}: {
  control: Control<PlanInput>;
  saved: PlanDto['states'] | undefined;
}) {
  const { field, fieldState } = useController({ control, name: 'states' });
  const rows: PlanInput['states'] = Array.isArray(field.value) ? field.value : [];
  const selected = rows.map((r) => r.state);
  const statusOf = (state: string) =>
    rows.find((r) => r.state === state)?.status ??
    saved?.find((s) => s.state === state)?.status ??
    'DEMO';
  const error = fieldState.error;

  return (
    <FormControl error={Boolean(error)} required>
      <InputLabel id="plan-states-label">States</InputLabel>
      <Select
        multiple
        labelId="plan-states-label"
        id="plan-states"
        label="States"
        name={field.name}
        inputRef={field.ref}
        onBlur={field.onBlur}
        value={selected}
        onChange={(e) => {
          const codes =
            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          field.onChange(codes.map((state) => ({ state, status: statusOf(state) })));
        }}
        renderValue={() =>
          rows.length > 6
            ? `${rows.length} selected`
            : rows.map((r) => `${r.state} (${statusOf(r.state)})`).join(', ')
        }
        MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
      >
        {STATE_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value} dense>
            <Checkbox size="small" checked={selected.includes(o.value)} />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {error
          ? (error.message ?? 'Check the selected states')
          : 'A new state starts as DEMO. Set it LIVE from the plan list.'}
      </FormHelperText>
    </FormControl>
  );
}

/** Add Plan and Edit Plan (PC232). Fields, labels and their order follow the reference form. */
export function PlanFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const plan = usePlan(id);
  const codeOptions = useCarrierCodeOptions();
  const planOptions = usePlanOptions();
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const routing = useFeatureReleased()('zip-carrier-routing');

  const { control, handleSubmit, reset, setError, setFocus, setValue, getValues } =
    useForm<PlanInput>({
      resolver,
      defaultValues: NEW_PLAN,
    });

  // What the name and tax fields last held, so that loading a plan is not taken for typing.
  const seen = useRef<{ name: string | undefined; taxable: boolean | undefined }>({
    name: undefined,
    taxable: undefined,
  });
  useEffect(() => {
    if (!plan.data) return;
    seen.current = { name: plan.data.name, taxable: plan.data.taxable };
    reset(toFormValues(plan.data));
  }, [plan.data, reset]);

  const taxable = useWatch({ control, name: 'taxable' });
  const isUnlimited = useWatch({ control, name: 'isUnlimited' });
  const planName = useWatch({ control, name: 'name' });
  const fallbackId = useWatch({ control, name: 'fallbackPlanId' });

  // As the reference: typing the name fills the description; a tax status of Yes starts the
  // tax block at Prepaid Wireless(14) and the first customer type. Only on the user's changes,
  // not when a plan is loaded.
  useEffect(() => {
    if (planName === seen.current.name) return;
    const first = seen.current.name === undefined;
    seen.current.name = planName;
    if (!first) setValue('description', planName ?? '');
  }, [planName, setValue]);
  useEffect(() => {
    if (taxable === seen.current.taxable) return;
    const first = seen.current.taxable === undefined;
    seen.current.taxable = taxable;
    if (!first && taxable) {
      setValue('businessType', '14');
      setValue('customerType', '01');
    }
  }, [taxable, setValue]);
  const lineCount = lineCountOf(useWatch({ control, name: 'maxLines' }));

  // The two effects below read the form's current values, not the watched ones: right after
  // `reset` loads a plan the watched values are one render behind and would wipe what was loaded.

  // The unlimited options only apply to an unlimited plan.
  useEffect(() => {
    if (getValues('isUnlimited')) return;
    setValue('unlimitedData', false);
    setValue('unlimitedText', false);
    setValue('unlimitedMinutes', false);
  }, [isUnlimited, getValues, setValue]);

  // One price per line for a family plan, none for a single-line plan.
  useEffect(() => {
    const count = lineCountOf(getValues('maxLines'));
    const current = getValues('linePricesCents') ?? [];
    if (current.length === count) return;
    // A line without a price yet holds `null` (see `resolver`).
    setValue(
      'linePricesCents',
      Array.from({ length: count }, (_, i) => current[i] ?? null) as number[],
    );
  }, [lineCount, getValues, setValue]);

  const submit = handleSubmit(async (values) => {
    // The resolver hands back the parsed values, with the schema defaults filled in.
    const data = values as PlanData;
    const input: PlanInput = {
      ...data,
      // Hidden inputs are not sent: tax lines of a non-taxable plan, empty tax lines.
      taxLines: data.taxable
        ? data.taxLines.filter((l) => l.amountOrPercent !== '' || l.transCode !== '')
        : [],
      linePricesCents: data.maxLines > 1 ? data.linePricesCents : [],
      // ZIP carrier routing: none on the main code's carrier; while it is off, left as stored.
      moreCarrierCodeIds: routing
        ? (data.moreCarrierCodeIds ?? []).filter(
            (codeId) =>
              (codeOptions.data ?? []).find((o) => o.id === codeId)?.carrierCode !==
              (codeOptions.data ?? []).find((o) => o.id === data.carrierCodeId)?.carrierCode,
          )
        : undefined,
    };
    try {
      if (id) {
        const updated = await updatePlan.mutateAsync({ id, input });
        notify.success(`Plan "${updated.name}" updated.`);
      } else {
        const created = await createPlan.mutateAsync(input);
        notify.success(`Plan "${created.name}" created with plan code ${created.code}.`);
      }
      navigate(LIST_PATH);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
      else if (err instanceof ApiError && err.details[0])
        // The form is long: bring the first rejected field into view.
        setFocus(err.details[0].path as FieldPath<PlanInput>);
    }
  });

  const title = id ? 'Edit Plan' : 'Add Plan';
  const loadError = (id ? plan.error : null) ?? codeOptions.error ?? planOptions.error;
  if (loadError)
    return (
      <Box>
        <PageHeader title={title} code="PC232" />
        <ErrorState
          error={loadError}
          onRetry={() => {
            if (id) void plan.refetch();
            void codeOptions.refetch();
            void planOptions.refetch();
          }}
        />
      </Box>
    );
  // Selects need their options before they can show a saved value.
  if ((id && plan.isPending) || codeOptions.isPending || planOptions.isPending)
    return (
      <Box>
        <PageHeader title={title} code="PC232" />
        <LoadingState />
      </Box>
    );

  const saved = id ? plan.data : undefined;
  const carrierCodeOptions = withCurrent(
    (codeOptions.data ?? [])
      .filter((o) => o.type === 'Service Plan')
      .map((o) => ({ value: o.id, label: `${o.code} (${o.carrierCode})` })),
    // A plan whose carrier code was deactivated still shows which code it has.
    saved
      ? {
          value: saved.carrierCode.id,
          label: `${saved.carrierCode.code} (${saved.carrierCode.carrier.code})`,
        }
      : undefined,
  );
  const fallbackOptions = (planOptions.data ?? [])
    .filter((p) => p.id !== id)
    .map((p) => ({ value: p.id, label: p.name }));
  const paidFallback = (planOptions.data ?? []).some(
    (p) => p.id === fallbackId && p.priceCents > 0,
  );
  const submitting = createPlan.isPending || updatePlan.isPending;
  const fullRow = { gridColumn: '1 / -1' };

  return (
    <Box>
      <PageHeader title={title} code="PC232" />
      <Paper
        variant="outlined"
        component="form"
        noValidate
        onSubmit={(e) => void submit(e)}
        aria-label={title}
        sx={{ p: 2 }}
      >
        {saved ? (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Plan code {saved.code}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            alignItems: 'start',
          }}
        >
          <TextInput control={control} name="name" label="Plan Name" required />
          <TextInput control={control} name="description" label="Plan Description" />
          <SelectInput
            control={control}
            name="type"
            label="Plan Type"
            required
            options={toOptions(PLAN_TYPES)}
          />

          <Box sx={fullRow}>
            <ChannelsInput control={control} />
          </Box>

          <YesNoInput
            control={control}
            name="taxable"
            label="Is the plan subject to applicable taxes?"
          />
          <SelectInput
            control={control}
            name="customerType"
            label="Customer Type"
            options={CUSTOMER_TYPE_OPTIONS}
          />
          <SelectInput
            control={control}
            name="businessType"
            label="Business Type"
            options={BUSINESS_TYPE_OPTIONS}
          />
          <YesNoInput
            control={control}
            name="priceIncludesTax"
            label="Does the plan price include taxes?"
          />

          {taxable
            ? Array.from({ length: TAX_LINE_COUNT }, (_, i) => (
                <Box key={i} sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr' }}>
                  <TextInput
                    control={control}
                    name={`taxLines.${i}.amountOrPercent`}
                    label={`Amount/Percent ${i + 1}`}
                    slotProps={{ htmlInput: { maxLength: 20 } }}
                  />
                  <TextInput
                    control={control}
                    name={`taxLines.${i}.transCode`}
                    label={`Tax transaction code ${i + 1}`}
                    slotProps={{ htmlInput: { maxLength: 8, inputMode: 'numeric' } }}
                  />
                </Box>
              ))
            : null}

          <Box
            sx={{ ...fullRow, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}
          >
            <Box sx={{ width: { xs: '100%', sm: 260 } }}>
              <YesNoInput control={control} name="isUnlimited" label="Is Unlimited Plan" />
            </Box>
            <FormGroup row>
              <CheckboxInput
                control={control}
                name="unlimitedData"
                label="Data Unlimited"
                disabled={!isUnlimited}
              />
              <CheckboxInput
                control={control}
                name="unlimitedText"
                label="Text Unlimited"
                disabled={!isUnlimited}
              />
              <CheckboxInput
                control={control}
                name="unlimitedMinutes"
                label="Minute Unlimited"
                disabled={!isUnlimited}
              />
            </FormGroup>
          </Box>

          <SelectInput
            control={control}
            name="carrierCodeId"
            label="Carrier Setup"
            required
            options={carrierCodeOptions}
          />
          {routing ? (
            <OtherCarrierCodes
              control={control}
              options={[
                ...(codeOptions.data ?? []).filter((o) => o.type === 'Service Plan'),
                // A code that was deactivated still shows on the plan that has it.
                ...(saved?.moreCarrierCodes ?? [])
                  .filter((c) => !(codeOptions.data ?? []).some((o) => o.id === c.id))
                  .map((c) => ({
                    id: c.id,
                    code: c.code,
                    carrierCode: c.carrier.code,
                    type: 'Service Plan' as const,
                  })),
              ]}
            />
          ) : null}
          <MoneyInput control={control} name="priceCents" label="Plan Price" required />
          <MoneyInput control={control} name="activationFeeCents" label="Activation Fee" />
          <NumberInput
            control={control}
            name="maxLines"
            label="Maximum number of lines allowed"
            emptyValue={null}
          />
          {Array.from({ length: lineCount }, (_, i) => (
            <MoneyInput
              key={i}
              control={control}
              name={`linePricesCents.${i}`}
              label={`Price for ${ordinal(i + 1)} line`}
              required
            />
          ))}

          <YesNoInput control={control} name="tribal" label="Tribal Plan" nullable />
          <YesNoInput control={control} name="display" label="Display Status" required />
          <YesNoInput
            control={control}
            name="notifyCustomer"
            label="Is customer notification required"
            required
          />
          <YesNoInput control={control} name="byod" label="Is Byod" nullable />
          <SelectInput
            control={control}
            name="nladServiceType"
            label="Plan Service Type (For NLAD enroll submission)"
            required
            options={toOptions(NLAD_SERVICE_TYPES)}
          />
          <NumberInput
            control={control}
            name="gracePeriodDays"
            label="Grace Period Days"
            required
            emptyValue={null}
            helperText="Kept with the plan. What happens when a plan runs out follows the expiry attempts in System Config."
          />
          <SelectInput
            control={control}
            name="statusDuringGrace"
            label="Account status During Grace Period"
            options={toOptions(GRACE_STATUSES_DURING)}
          />
          <SelectInput
            control={control}
            name="statusAfterGrace"
            label="Account status after Grace Period expiration"
            options={toOptions(GRACE_STATUSES_AFTER)}
          />
          <SelectInput
            control={control}
            name="fallbackPlanId"
            label="Change to this plan in case of non-payment"
            options={fallbackOptions}
            emptyLabel="Select"
          />
          {paidFallback ? (
            <Alert severity="warning" sx={fullRow}>
              Selected degrade plan is a paid plan. System will not move non payment subscriber to
              paid plan. you must select free plan.
            </Alert>
          ) : null}

          <Box sx={fullRow}>
            <StatesInput control={control} saved={saved?.states} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Submit
          </Button>
          <Button component={Link} to={LIST_PATH} disabled={submitting}>
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
